import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useParams, useHistory } from 'react-router-dom';
import Popover from 'react-text-selection-popover';
import Fade from '@material-ui/core/Fade';

import { useCodingQueueDetails } from '../../../../../modules/hooks/codingqueuedetail';
import EncounterCard from '../../../../../components/encountercard/components/encountercard';
import AddDxDialog from '../../../../../components/adddxdialog/components/adddxdialog';
import AddCommentToPatientDialog from '../../../../../components/addcommenttopatientdialog/addcommenttopatientdialog';
import AddReasonOptionDialog from '../../../../../components/addreasonoptiondialog/addreasonoptiondialog';
import HeaderInfoSection from '../../../../../components/headerinfosection/components/headerinfosection';
import CommentSection from '../../../../../components/commentsection/components/commentsection';
import WorkflowSection from '../../../../../components/workflowsection/components/workflowsection';
import TextSelectionMenu from '../../../../../components/textselectionmenu/textselectionmenu';
import CollapsableSection from '../../../../../components/collapsablesection/components/collapsablesection';
import LoadingDialog from '../../../../../components/loadingdialog/components/loadingdialog';
import DxSection from '../../../../../components/dxsection/components/dxsection';
import Lookback from '../../../../../components/lookback/components/lookback';
import PatientHistorySection from '../../../../../components/patienthistorysection/components/patienthistorysection';
import OnHoldDialog from '../../../../../components/onholddialog/onholddialog';
import HoldSection from '../../../../../components/holdsection/holdsection';
import HoldReviewDialog from '../../../../../components/holdreviewdialog/holdreviewdialog';
import { GlobalHotKeys } from '../../../../../../node_modules/react-hotkeys';
import useBroadcastChannel from '../../../../../components/popoutcard/usebroadcastchannel';

const rootStyle = {
  height: '100%'
};

const leftColumnStyle = {
  height:'85%',
  overflowY:'hidden',
  width:'62%',
  display: 'inline-block',
  border: '1px solid #000'
};

const rightColumnStyle = {
  display: 'inline-block',
  width: '35%',
  verticalAlign:'top',
  position: 'relative',
  maxHeight: '85%',
  overflowY: 'scroll',
  border: '1px solid #000',
  overflowX: 'hidden'
};

const rightColumnSectionStyle = {
  display: 'inline-block',
  width: '100%'
};

const titleBarStyle = {
  color: '#fff',
  backgroundColor: '#303f9f',
  height: '33px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end'
};

const headerStyle = {
  color: '#fff',
  backgroundColor: '#303f9f',
  height: '33px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 0.35em'
};

const notesAreaRef = React.createRef();

const popoutInitialState = { isPoppedOut: false };

const pluckMessage = original => original.data;

const Details = ({
  service
}) => {
  const { encounterId } = useParams();
  const history = useHistory();

  const [state, boundActions] = useCodingQueueDetails(service, encounterId);
  const [popoutState, setPopoutState] = useState(popoutInitialState);

  const broadcastHandler = channel => e => {
    const message = pluckMessage(e);

    switch (message.type) {
      case 'ADD_DX': {
        const { payload } = message;

        channel.postMessage({
          type: 'SHOW_LOADING',
          payload: { statusMessage: 'Adding DX' }
        });

        boundActions.addDx(
          payload.encounterId,
          payload.cmsCodeId,
          payload.supportingText,
          payload.reasonOptions
        )
        .then(() => {
          channel.postMessage({ type: 'HIDE_LOADING' });
          channel.postMessage({ type: 'CLOSE_ADD_DX_DIALOG' });
        });

        return;
      }

      default: {
        return;
      }
    }
  };

  const [postMessage] = useBroadcastChannel('foo', broadcastHandler);

  const [windowRef, setWindowRef] = useState(null);

  const {
    details: {
      additionalInfo: { dateOfService },
      comments,
      currentEncounterInfo: {
        isOnHold,
        isFinalized,
        patientId
      },
      dxList,
      holdData,
      encounterCard: { encounterText },
      encounterList,
      reasonOptions,
      patientHistory,
      encounterHoldReasonOptions,
      canRemoveHold,
      encounterAuditReasons
    },
    addDxDialog,
    addCommentDialog,
    addReasonOptionDialog,
    holdDialog,
    holdReviewDialog,
    lookback,
    auditResolutionDialog,
    isLoading
  } = state;

  const keyMap = {
    CLOSE_DIALOG: ["Escape"],
    OPEN_COMMENTS: ["Alt+c", "Alt+C"],
    TOGGLE_POPOUT: ['p', 'P']
  };

  const keyHandlers = {
    CLOSE_DIALOG: event => {
      if (addCommentDialog.isOpen)
        boundActions.closeAddCommentDialog();

      if (addReasonOptionDialog.isOpen)
        boundActions.closeAddReasonOptionDialog();

      if (addDxDialog.isOpen)
        boundActions.closeAddDxDialog();

      if (holdDialog.isOpen)
        boundActions.closeHoldDialog();

      if (holdReviewDialog.isOpen)
        boundActions.closeHoldReviewDialog();
    },
    TOGGLE_POPOUT: () => {
      if (!popoutState.isPoppedOut) {
        const url = service.serviceRoutes.gotoPopout(encounterId);
        setWindowRef(window.open(url));
        setPopoutState(prev => ({ ...prev, isPoppedOut: true }));
      } else {
        windowRef.close();
        setPopoutState(prev => ({ ...prev, isPoppedOut: false }))
      }
    }
  }

  return (
    <GlobalHotKeys keyMap={keyMap} handlers={keyHandlers} allowChanges={true}>
      <div style={rootStyle}>
        <Popover selectionRef={notesAreaRef}>
          <TextSelectionMenu
            openAddDxDialog={boundActions.openAddDxDialog}
            dateOfService={dateOfService}
          />
        </Popover>
        <div style={leftColumnStyle}>
          <div style={headerStyle}>
            <div>
              <label>
                {
                  lookback.isVisible
                    ? 'Encounter List'
                    : 'Notes'
                }
              </label>
            </div>

            <div>
              <button
                onClick={() =>
                  lookback.isVisible
                    ? boundActions.hideLookback()
                    : boundActions.showLookback()
                }
              >
                <span>
                  {
                    lookback.isVisible ? 'Show Notes' : 'Show Encounter List'
                  }
                </span>
              </button>
            </div>
          </div>

          <div>
            <Fade in={lookback.isVisible}>
              <div style={{ display: lookback.isVisible ? 'block' : 'none' }}>
                <Lookback
                  encounterList={encounterList}
                  dxFilterValue={lookback.dxFilterValue}
                  hccFilterValue={lookback.hccFilterValue}
                  handleLookbackFilterChange={boundActions.setLookbackFilterValue}
                  redirectToReviewDetails={boundActions.redirectToReviewDetails}
                />
              </div>
            </Fade>

            {/* EncounterCard */}
            <Fade in={!lookback.isVisible}>
              <div style={{ display: !lookback.isVisible ? 'block' : 'none' }}>
                <EncounterCard
                  noteMarkup={encounterText}
                  forwardRef={notesAreaRef}
                  dateOfService={dateOfService}
                />
              </div>
            </Fade>
          </div>
        </div>

        <div style={rightColumnStyle}>
          <div style={rightColumnSectionStyle}>
            <CollapsableSection title="Patient History">
              <PatientHistorySection historyData={patientHistory} />
            </CollapsableSection>
          </div>

          {
            holdData.holdData && holdData.holdData.length > 0
              ? (
                <div style={rightColumnSectionStyle}>
                  <CollapsableSection title="Hold Data" shouldStartOpened={true}>
                    <HoldSection
                      holdData={holdData.holdData}
                      openReviewDialog={boundActions.openHoldReviewDialog}
                    />
                  </CollapsableSection>
                </div>
              )
              : null
          }

          <div style={rightColumnSectionStyle}>
            <CollapsableSection title="Dxes" shouldStartOpened={true}>
              <div>
                <DxSection
                  isAudit={true}
                  encounterId={encounterId}
                  listItems={dxList.listItems}
                  openAddDxDialog={boundActions.openAddDxDialog}
                  openAddReasonOptionDialog={boundActions.openAddReasonOptionDialog}
                  unapproveDx={boundActions.unapproveDx}
                  unremoveDx={boundActions.unremoveDx}
                  encounterAuditReasons={encounterAuditReasons}
                  isMultiselect={dxList.isMultiselect}
                  multiselectItems={dxList.multiselectItems}
                  beginMultiselect={boundActions.beginMultiselect}
                  endMultiselect={boundActions.endMultiselect}
                  selectItemMultiselect={boundActions.selectItemMultiselect}
                  deselectItemMultiselect={boundActions.deselectItemMultiselect}
                  dateOfService={dateOfService}
                  isAuditResolutionDialogVisible={auditResolutionDialog.isOpen}
                  openAuditResolutionDialog={
                    boundActions.openAuditResolutionDialog
                  }
                  closeAuditResolutionDialog={
                    boundActions.closeAuditResolutionDialog
                  }
                  resolveAuditReason={boundActions.resolveAuditReason}
                  selectedAuditReasonId={
                    auditResolutionDialog.selectedAuditReasonId
                  }
                  selectedAuditReasonIsResolvedValue={
                    auditResolutionDialog.selectedAuditReasonIsResolvedValue
                  }
                  auditResolutionOptions={
                    auditResolutionDialog.auditResolutionOptions
                  }
                  selectAuditResolutionOption={
                    boundActions.selectAuditResolutionOption
                  }
                  selectedAuditResolutionOptionId={
                    auditResolutionDialog.selectedAuditResolutionOptionId
                  }
                  hydrateEncounterAuditReasons={
                    boundActions.hydrateEncounterAuditReasons
                  }
                />
              </div>
            </CollapsableSection>
          </div>

          <CollapsableSection title="Comments" titleCount={comments.length}>
            <div>
              <CommentSection
                comments={comments}
                openAddCommentDialog={boundActions.openAddCommentDialog}
                isAudit={true}
                isReview={false}
              />
            </div>
          </CollapsableSection>

          <CollapsableSection title="Additional Info">
            <div>
              <HeaderInfoSection
                encounterId={encounterId}
                {...state.details.additionalInfo}
              />
            </div>
          </CollapsableSection>
        </div>

        <WorkflowSection
          encounterId={encounterId}
          isOnHold={isOnHold}
          removeFromHold={boundActions.removeFromHold}
          placeOnHold={boundActions.placeOnHold}
          isFinalized={isFinalized}
          finalizeEncounter={boundActions.finalizeEncounter}
          unfinalizeEncounter={boundActions.unfinalizeEncounter}
          exitEncounter={boundActions.exitEncounter}
          doRedirect={history.push}
          isAudit={true}
          completeEncounter={boundActions.completeEncounter}
          canRemoveHold={canRemoveHold}
          isLoading={isLoading}
          encounterAuditReasons={encounterAuditReasons}
        />

        <LoadingDialog
          isOpen={state.isLoading}
          loadingMessage={state.loadingMessage}
        />

        <AddDxDialog
          isOpen={addDxDialog.isOpen}
          dateOfService={dateOfService}
          encounterId={encounterId}
          supportingText={addDxDialog.supportingText}
          reasonOptions={reasonOptions}
          addDx={boundActions.addDx}
          closeDialog={boundActions.closeAddDxDialog}
        />

        <AddCommentToPatientDialog
          isOpen={addCommentDialog.isOpen}
          closeDialog={boundActions.closeAddCommentDialog}
          commentSourceId={patientId}
          commentEncounterId={encounterId}
          addCommentToPatient={boundActions.addCommentToPatient}
          addCommentToEncounter={boundActions.addCommentToEncounter}
          updateComment={boundActions.saveComment}
          deleteComment={boundActions.deleteComment}
          setCommentInactive={boundActions.setCommentInactive}
          dateOfService={dateOfService}
          commentId={addCommentDialog.commentId}
          commentContent={addCommentDialog.commentText}
          commentType={addCommentDialog.commentType}
          editMode={addCommentDialog.editMode}
          isActive={addCommentDialog.isActive}
          canBeDeleted={addCommentDialog.canBeDeleted}
          canBeEdited={addCommentDialog.canBeEdited}
          isHoldComment={addCommentDialog.isHoldComment}
        />

        <AddReasonOptionDialog
          isOpen={addReasonOptionDialog.isOpen}
          mode={addReasonOptionDialog.mode}
          reasonOptions={reasonOptions}
          closeDialog={boundActions.closeAddReasonOptionDialog}
          encounterId={encounterId}
          encounterCmsCodeId={addReasonOptionDialog.encounterCmsCodeId}
          performApproveRemove={
            addReasonOptionDialog.mode === 'Approve' ?
              boundActions.approveDx :
              boundActions.removeDx
          }
        />

        <OnHoldDialog
          isOpen={holdDialog.isOpen}
          encounterId={encounterId}
          closeDialog={boundActions.closeHoldDialog}
          doRedirect={history.push}
          submitHold={boundActions.submitHold}
          holdReasons={encounterHoldReasonOptions}
        />

        <HoldReviewDialog
          isOpen={holdReviewDialog.isOpen}
          closeDialog={boundActions.closeHoldReviewDialog}
          encounterId={encounterId}
          submitHoldReview={boundActions.markAsReviewed}
        />
      </div>
    </GlobalHotKeys>
  );
}

Details.propTypes = {
  service: PropTypes.object.isRequired
};

export default Details;
